import { LIFECYCLE_EVENTS, type StreamedMessage } from "@unbox/api";
import { createContext, useContext, useEffect, useRef } from "react";
import { SSE } from "sse.js";
import { impersonationHeaders } from "@/lib/impersonation";
import { useMerchant } from "@/providers/MerchantProvider";

interface SSEContextValue {
  isConnected: boolean;
  subscribe: (callback: (event: StreamedMessage) => void) => () => void;
}

const SSEContext = createContext<SSEContextValue | undefined>(undefined);

export const SSEProvider = ({ children }: { children: React.ReactNode }) => {
  const isConnected = useRef(false);
  const eventSourceRef = useRef<SSE | null>(null);
  const subscribersRef = useRef<Set<(event: StreamedMessage) => void>>(new Set());
  const initializingRef = useRef(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const merchant = useMerchant();

  useEffect(() => {
    // Cancellation flag for this effect instance (prevents race conditions with StrictMode)
    let cancelled = false;

    const cleanup = () => {
      cancelled = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      isConnected.current = false;
      initializingRef.current = false;
    };

    const init = async () => {
      // Prevent duplicate initialization
      if (cancelled || initializingRef.current || eventSourceRef.current) return;
      initializingRef.current = true;

      try {
        const eventSource = new SSE(`${import.meta.env.VITE_API_URL}/sse?merchantId=${merchant}`, {
          headers: impersonationHeaders(),
          withCredentials: true,
          autoReconnect: false,
        });
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
          isConnected.current = true;
        };

        eventSource.onerror = () => {
          isConnected.current = false;
          eventSource.close();
          eventSourceRef.current = null;
          initializingRef.current = false;

          if (cancelled) return;

          reconnectTimeoutRef.current = setTimeout(() => {
            init();
          }, 2000);
        };

        eventSource.onmessage = (event) => {
          const payload = JSON.parse(event.data) as StreamedMessage;
          if (LIFECYCLE_EVENTS.includes(payload.event)) return;
          subscribersRef.current.forEach((callback) => callback(payload));
        };
      } catch (error) {
        initializingRef.current = false;
        if (cancelled) return;

        reconnectTimeoutRef.current = setTimeout(() => {
          init();
        }, 2000);
      }
    };

    init();

    return cleanup;
  }, [merchant]);

  const subscribe = (callback: (event: StreamedMessage) => void) => {
    subscribersRef.current.add(callback);
    return () => {
      subscribersRef.current.delete(callback);
    };
  };

  return <SSEContext.Provider value={{ isConnected: isConnected.current, subscribe }}>{children}</SSEContext.Provider>;
};

export const useSSE = (callback: (event: StreamedMessage) => void) => {
  const context = useContext(SSEContext);

  if (!context) {
    throw new Error("useSSE must be used within SSEProvider");
  }

  useEffect(() => {
    return context.subscribe(callback);
  }, [callback, context]);

  return { isConnected: context.isConnected };
};
